<div class="space-y-6">
  <!-- Inputs -->
  <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
    <div class="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 class="flex items-center gap-2 text-lg font-semibold text-[#1E2A78]">
          {{ 'CREDIT_REPAYMENT_CALC.INPUTS_TITLE' | translate }}

          <span
            *ngIf="loadingInputs"
            class="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-600"
          >
            <svg class="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            {{ 'COMMON.LOADING' | translate }}
          </span>
        </h3>

        <p class="mt-1 text-sm text-gray-500">
          {{ 'CREDIT_REPAYMENT_CALC.INPUTS_SUBTITLE' | translate }}
        </p>
      </div>

      <button
        type="button"
        [disabled]="loadingInputs"
        (click)="refreshInputs()"
        class="h-11 shrink-0 rounded-xl border border-indigo-200 bg-white px-4 text-sm font-medium text-[#1E2A78] transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ (loadingInputs ? 'COMMON.LOADING' : 'COMMON.REFRESH') | translate }}
      </button>
    </div>

    <div
      *ngIf="loadErrorMessage"
      class="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
    >
      {{ loadErrorMessage }}
    </div>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div class="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">{{ 'CREDIT_REPAYMENT_CALC.GRACE_PERIOD' | translate }}</p>
        <p class="mt-1 text-lg font-bold text-[#1E2A78]">{{ gracePeriod }}</p>
      </div>

      <div class="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">
          {{ 'CREDIT_REPAYMENT_CALC.NO_OF_INSTALLMENTS' | translate }}
        </p>
        <p class="mt-1 text-lg font-bold text-[#1E2A78]">{{ numberOfInstallments }}</p>
      </div>

      <div class="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">{{ 'CREDIT_REPAYMENT_CALC.FREQUENCY' | translate }}</p>
        <p class="mt-1 text-lg font-bold text-[#1E2A78]">{{ selectedFrequencyLabel | translate }}</p>
      </div>

      <div class="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">{{ 'CREDIT_REPAYMENT_CALC.IN_ARREARS' | translate }}</p>
        <p class="mt-1 text-lg font-bold text-[#1E2A78]">
          {{ (arrearFlg === 1 ? 'COMMON.YES' : 'COMMON.NO') | translate }}
        </p>
      </div>

      <div class="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">
          {{ 'CREDIT_REPAYMENT_CALC.PREV_MAX_EXPOSURE' | translate }}
        </p>
        <p class="mt-1 text-lg font-bold text-emerald-800">
          PKR {{ prevMaxExposure | number: '1.0-2' }}
        </p>
      </div>

      <div class="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">
          {{ 'CREDIT_REPAYMENT_CALC.TOTAL_PROPOSED_DISBURSEMENT' | translate }}
        </p>
        <p class="mt-1 text-lg font-bold text-emerald-800">
          PKR {{ totalDisbursementAmount | number: '1.0-2' }}
        </p>
      </div>

      <div class="rounded-xl border border-amber-100 bg-amber-50 p-4 sm:col-span-2">
        <p class="text-xs font-semibold uppercase text-slate-500">
          {{ 'CREDIT_REPAYMENT_CALC.PROPOSED_MAX_EXPOSURE' | translate }}
        </p>
        <p class="mt-1 text-lg font-bold text-amber-800">
          PKR {{ proposedMaxExposure | number: '1.0-2' }}
        </p>
      </div>
    </div>
  </div>

  <!-- Disbursements -->
  <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
    <div
      class="flex flex-col gap-3 border-b border-gray-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h3 class="text-lg font-semibold text-[#1E2A78]">{{ 'CREDIT_REPAYMENT_CALC.PROPOSED_DISBURSEMENTS' | translate }}</h3>
        <p class="mt-1 text-sm text-gray-500">
          @if (editable) {
            {{ 'CREDIT_REPAYMENT_CALC.EDITABLE_HINT' | translate }}
          } @else {
            {{ 'CREDIT_REPAYMENT_CALC.READONLY_HINT' | translate }}
          }
        </p>
      </div>
    </div>

    @for (group of groupedDisbursementRows; track group.termRefCdSeq) {
      <div class="border-b border-gray-200 px-6 py-5 last:border-b-0">
        <h4 class="mb-3 text-sm font-semibold text-[#1E2A78]">{{ group.title }}</h4>

        <div class="overflow-x-auto rounded-xl border border-gray-100">
          <table class="w-full min-w-[520px]">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="w-20 border-b border-gray-200 px-5 py-3 text-center text-sm font-semibold text-gray-700"
                >
                  {{ 'CREDIT_REPAYMENT_CALC.S_NO' | translate }}
                </th>
                <th
                  class="border-b border-gray-200 px-5 py-3 text-start text-sm font-semibold text-gray-700"
                >
                  {{ 'CREDIT_REPAYMENT_CALC.YEAR_AND_MONTH' | translate }}
                </th>
                <th
                  class="border-b border-gray-200 px-5 py-3 text-end text-sm font-semibold text-gray-700"
                >
                  {{ 'CREDIT_REPAYMENT_CALC.PROPOSED_DISBURSEMENT_AMOUNT' | translate }}
                </th>
              </tr>
            </thead>

            <tbody>
              @for (entry of group.rows; track entry.index) {
                <tr class="border-b border-gray-100 last:border-b-0">
                  <td class="px-5 py-4 text-center font-medium text-gray-700">
                    {{ entry.number }}
                  </td>

                  <td class="px-5 py-4 text-gray-700">
                    @if (editable) {
                      <input
                        type="date"
                        [(ngModel)]="entry.row.schMth"
                        [name]="'disbursementDate_' + entry.index"
                        class="w-full min-w-[150px] rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                        (keydown)="blockManualDateEntry($event)"
                        (paste)="$event.preventDefault()"
                      />
                    } @else {
                      {{ entry.row.schMth || '-' }}
                    }
                  </td>

                  <td class="px-5 py-4 text-end font-semibold text-gray-800">
                    @if (editable) {
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        [(ngModel)]="entry.row.prpsdDsbmntAmt"
                        [name]="'disbursementAmount_' + entry.index"
                        class="w-full min-w-[140px] rounded-lg border border-gray-300 px-3 py-2 text-end text-sm text-gray-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      />
                    } @else {
                      {{ entry.row.prpsdDsbmntAmt | number: '1.0-2' }}
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }

    @if (!loadingInputs && disbursementRows.length === 0) {
      <div class="px-5 py-6 text-center text-sm text-gray-400">
        {{ 'CREDIT_REPAYMENT_CALC.NO_DISBURSEMENTS' | translate }}
      </div>
    }
  </div>

  <!-- Messages -->
  <div
    *ngIf="errorMessage"
    class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
  >
    {{ errorMessage }}
  </div>

  <div
    *ngIf="successMessage"
    class="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700"
  >
    {{ successMessage }}
  </div>

  <!-- Calculate Button -->
  <div *ngIf="editable" class="flex justify-end">
    <button
      type="button"
      [disabled]="calculating || loadingInputs"
      (click)="calculateRepaymentPlan()"
      class="rounded-xl bg-[#1E2A78] px-6 py-3 font-medium text-white shadow-sm transition hover:bg-[#16205e] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {{ (calculating ? 'CREDIT_REPAYMENT_CALC.CALCULATING' : 'CREDIT_REPAYMENT_CALC.CALCULATE') | translate }}
    </button>
  </div>

  <!-- API Result Summary -->
  <div
    *ngIf="resultSummary.length > 0"
    class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
  >
    <h3 class="mb-5 text-lg font-semibold text-[#1E2A78]">{{ 'CREDIT_REPAYMENT_CALC.SUMMARY_TITLE' | translate }}</h3>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div
        *ngFor="let item of resultSummary; trackBy: trackByIndex"
        class="rounded-xl border border-gray-200 bg-gray-50 p-4"
      >
        <p class="text-xs font-medium text-gray-500">{{ item.label | translate }}</p>
        <p class="mt-1 font-semibold text-gray-800">{{ item.value }}</p>
      </div>
    </div>
  </div>

  <!-- API Result Table -->
  <div
    *ngIf="resultRows.length > 0"
    class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
  >
    <div class="border-b border-gray-200 px-6 py-5">
      <h3 class="text-lg font-semibold text-[#1E2A78]">{{ 'CREDIT_REPAYMENT_CALC.CALCULATED_PLAN' | translate }}</h3>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full min-w-[850px]">
        <thead>
          <tr class="bg-[#1E2A78] text-white">
            <th
              class="w-16 border-e border-indigo-400 px-4 py-3 text-center text-sm font-semibold"
            >
              {{ 'CREDIT_REPAYMENT_CALC.S_NO' | translate }}
            </th>
            <th
              *ngFor="let column of resultColumns; trackBy: trackByColumn"
              class="border-e border-indigo-400 px-4 py-3 text-start text-sm font-semibold"
            >
              {{ formatColumnLabel(column) }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            *ngFor="let resultRow of resultRows; let resultIndex = index; trackBy: trackByIndex"
            class="border-b border-gray-200 last:border-b-0"
          >
            <td
              class="border-e border-gray-200 px-4 py-3 text-center font-medium text-gray-700"
            >
              {{ resultIndex + 1 }}
            </td>
            <td
              *ngFor="let column of resultColumns; trackBy: trackByColumn"
              class="border-e border-gray-200 px-4 py-3 text-sm text-gray-700"
            >
              {{ formatResultValue(resultRow[column]) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>
